import { gstime } from "satellite.js";
import { buildTle, EARTH_RADIUS_KM } from "./tle";

/* ------------------------------------------------------------------ *
 * The launch as the group sees it, on a compressed clock: countdown, then
 * the flight simulation from liftoff -- with the rocket's launch video
 * playing alongside during powered flight -- up to the target orbit, where
 * the CubeSat separates from the upper stage. The profile is a typical
 * small-satellite launch, simplified, not one specific rocket's.
 * ------------------------------------------------------------------ */

export const COUNTDOWN_S = 10;

// Real seconds of powered flight. It is stretched to the launch video, so
// footage and simulation reach orbit together; without a video it uses
// the default.
export const DEFAULT_POWERED_S = 45;
const MIN_POWERED_S = 25;
const MAX_POWERED_S = 240;

export function poweredSeconds(videoSeconds: number | null): number {
  if (videoSeconds === null || !Number.isFinite(videoSeconds) || videoSeconds <= 0) return DEFAULT_POWERED_S;
  return Math.min(MAX_POWERED_S, Math.max(MIN_POWERED_S, videoSeconds));
}

// Real seconds from reaching orbit to the separation, and from the
// separation until tracking data can be requested.
export const COAST_S = 8;
export const TRACKING_DELAY_S = 8;

// Mission time (seconds after liftoff) of reaching orbit and of separation.
export const ORBIT_MISSION_S = 540;
export const SEPARATION_MISSION_S = 600;
const STAGE_SEPARATION_MISSION_S = 155;
const FAIRING_MISSION_S = 200;
// After the separation the mission clock runs at ten times real time.
const AFTER_SEPARATION_SPEED = 10;

const MU_KM3_S2 = 398600.4418;

// Mission time for a given number of real seconds after liftoff.
export function missionSecondsAt(realSeconds: number, poweredS: number): number {
  if (realSeconds <= 0) return 0;
  if (realSeconds < poweredS) return (realSeconds / poweredS) * ORBIT_MISSION_S;
  if (realSeconds < poweredS + COAST_S) {
    return ORBIT_MISSION_S + ((realSeconds - poweredS) / COAST_S) * (SEPARATION_MISSION_S - ORBIT_MISSION_S);
  }
  return SEPARATION_MISSION_S + (realSeconds - poweredS - COAST_S) * AFTER_SEPARATION_SPEED;
}

function interpolate(points: [number, number][], x: number): number {
  if (x <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x0, y0] = points[i - 1];
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
  }
  return points[points.length - 1][1];
}

export function orbitalSpeedKmS(altitudeKm: number): number {
  return Math.sqrt(MU_KM3_S2 / (EARTH_RADIUS_KM + altitudeKm));
}

export function altitudeKmAt(missionS: number, orbitAltitudeKm: number): number {
  return interpolate(
    [
      [0, 0],
      [72, 12],
      [STAGE_SEPARATION_MISSION_S, 72],
      [FAIRING_MISSION_S, 115],
      [ORBIT_MISSION_S, orbitAltitudeKm],
    ],
    missionS
  );
}

export function speedKmSAt(missionS: number, orbitAltitudeKm: number): number {
  return interpolate(
    [
      [0, 0],
      [72, 0.45],
      [150, 2.3],
      [165, 2.25],
      [ORBIT_MISSION_S, orbitalSpeedKmS(orbitAltitudeKm)],
    ],
    missionS
  );
}

// Distance over the ground from the launch site.
export function downrangeKmAt(missionS: number, orbitAltitudeKm: number): number {
  const powered = interpolate(
    [
      [0, 0],
      [72, 8],
      [150, 75],
      [FAIRING_MISSION_S, 150],
      [ORBIT_MISSION_S, 1750],
    ],
    missionS
  );
  if (missionS <= ORBIT_MISSION_S) return powered;
  // Ground speed is a bit below orbital speed, by the ratio of the radii.
  const groundSpeed = (orbitalSpeedKmS(orbitAltitudeKm) * EARTH_RADIUS_KM) / (EARTH_RADIUS_KM + orbitAltitudeKm);
  return powered + groundSpeed * (missionS - ORBIT_MISSION_S);
}

export function flightStageAt(missionS: number): string {
  if (missionS < STAGE_SEPARATION_MISSION_S) return "First stage";
  if (missionS < ORBIT_MISSION_S) return "Second stage";
  if (missionS < SEPARATION_MISSION_S) return "Coasting";
  return "Separated";
}

export function ascentEvents(satelliteName: string): { missionS: number; text: string }[] {
  return [
    { missionS: 0, text: "Liftoff!" },
    { missionS: 72, text: "Max-Q: the greatest aerodynamic pressure on the rocket" },
    { missionS: 150, text: "Main engine cut-off (MECO)" },
    { missionS: STAGE_SEPARATION_MISSION_S, text: "Stage separation: the first stage falls away" },
    { missionS: 165, text: "Second-stage engine ignition" },
    { missionS: FAIRING_MISSION_S, text: "Fairing separation: the satellites are out in space" },
    { missionS: ORBIT_MISSION_S, text: "Second-stage engine cut-off: target orbit reached" },
    { missionS: 560, text: "Upper stage turns into position for separation" },
    { missionS: SEPARATION_MISSION_S, text: `Separation: ${satelliteName} is flying on its own` },
  ];
}

export function formatMissionClock(missionS: number): string {
  const total = Math.max(0, Math.floor(missionS));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `T+${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

// Small, stable number from the group id, so every member builds the very
// same TLE.
function hashOf(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

function dayOfYear(d: Date): number {
  return Math.floor((d.getTime() - Date.UTC(d.getUTCFullYear(), 0, 1)) / 86400000) + 1;
}

// TLE details a launch option may set (see supabase/sql/tle_details.sql).
// Anything left null is filled in with a realistic value of our own.
export type LaunchTleDetails = {
  intlDesignator: string; // "YYNNN", e.g. "26045"; "" = work it out
  catalogBase: number | null;
  raanDeg: number | null;
  argPerigeeDeg: number | null;
  meanAnomalyDeg: number | null;
  bstar: number | null;
  meanMotionDot: number | null;
};

export const NO_TLE_DETAILS: LaunchTleDetails = {
  intlDesignator: "",
  catalogBase: null,
  raanDeg: null,
  argPerigeeDeg: null,
  meanAnomalyDeg: null,
  bstar: null,
  meanMotionDot: null,
};

// Piece letters of an object from a launch: A, B, ... Z, AA, AB, ... --
// rideshares release dozens of objects, so later pieces get two letters.
function pieceLetters(index: number): string {
  const letter = (i: number) => String.fromCharCode(65 + i);
  return index < 26 ? letter(index) : letter(Math.floor(index / 26) - 1) + letter(index % 26);
}

// Stable pseudo-random angle (4 decimals, as in a TLE) from the group id.
function angleFrom(hash: number, salt: number): number {
  return Math.round(((Math.imul(hash ^ salt, 2654435761) >>> 0) % 3600000) / 10) / 1000;
}

// The satellite's first TLE, as tracking would report it after separation:
// the orbit the chosen rocket delivers to, at the moment of separation.
// What the launch option doesn't set is filled in realistically: the orbit
// plane from when and where the rocket lifted off (roughly: the launch
// site's position in space at liftoff), a catalog number in the range new
// objects get these days, the launch counted among the year's launches, and
// small typical values for drag. Deterministic for a group, so whichever
// member asks first, everyone gets the same TLE; groups on the same launch
// get different catalog numbers and piece letters, like rideshare objects.
export function tleAfterLaunch(params: {
  groupId: string;
  launchedAt: Date;
  launchSiteLonDeg: number | null;
  orbitAltitudeKm: number;
  orbitInclinationDeg: number;
  orbitEccentricity: number;
  details?: LaunchTleDetails;
}): { line1: string; line2: string } {
  const details = params.details ?? NO_TLE_DETAILS;
  const liftoff = new Date(params.launchedAt.getTime() + COUNTDOWN_S * 1000);
  const separatedAt = new Date(liftoff.getTime() + SEPARATION_MISSION_S * 1000);
  const hash = hashOf(params.groupId);
  const siderealDeg = ((gstime(liftoff) as number) * 180) / Math.PI;

  const designator = /^\d{5}$/.test(details.intlDesignator) ? details.intlDesignator : null;
  // Roughly 290 orbital launches a year: the launch number grows with the date.
  const launchNumber = designator ? Number(designator.slice(2)) : Math.min(999, Math.max(1, Math.round(dayOfYear(liftoff) * 0.8)));
  const launchYear = designator ? 2000 + Number(designator.slice(0, 2)) : liftoff.getUTCFullYear();

  return buildTle({
    // New objects in 2026 are numbered in the high 60,000s; without a base,
    // a number around there.
    catalogNumber: (details.catalogBase ?? 68000) + (hash % (details.catalogBase ? 200 : 1500)),
    launchYear,
    launchNumber,
    launchPiece: pieceLetters(hash % 60),
    epoch: separatedAt,
    bstar: details.bstar ?? 0.0003,
    meanMotionDot: details.meanMotionDot ?? 0.000015,
    // A brand-new object's first published element set.
    elementSetNumber: 999,
    inclinationDeg: params.orbitInclinationDeg,
    raanDeg: details.raanDeg ?? siderealDeg + (params.launchSiteLonDeg ?? 0),
    eccentricity: params.orbitEccentricity,
    argPerigeeDeg: details.argPerigeeDeg ?? angleFrom(hash, 0x51ed),
    meanAnomalyDeg: details.meanAnomalyDeg ?? angleFrom(hash, 0x2f7a),
    meanAltitudeKm: params.orbitAltitudeKm,
    revolutionNumber: 1,
  });
}
